import { Injectable, inject, DestroyRef } from '@angular/core';
import { translate } from '@jsverse/transloco';
import { Constants } from 'src/app/models/constants';
import { Instance } from 'src/app/models/instance/instance';
import { DateTimeService } from './date-time.service';
import { getDiffDays } from './utils';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class TrialStatusService {

  constructor(private dateTimeService: DateTimeService) {
  }

  /**
   * Prepares the trial status label to be displayed
   *
   * @param demoInstance - the demo instance for which the trial status is prepared
   * @returns the trial status
   */
  getTrialStatus(demoInstance: Instance): string {
    const expiresOn = !demoInstance.expirationDate ? this.getExpirationDate(demoInstance.dateCreated) : demoInstance.expirationDate;
    const remainingDays = this.dateTimeService.differenceBetweenDates(new Date(), expiresOn, Constants.TIME_UNIT_DAY);
    let label;

    if(remainingDays <= 0) {
      label = translate('SERVICE.TRIAL_EXPIRED');
    } else {
      label = translate('SERVICE.TRIAL_EXPIRES') + ' ' + remainingDays + ' ';
      label += remainingDays > 1 ? translate('SERVICE.TRIAL_EXPIRES_DAYS') : translate('SERVICE.TRIAL_EXPIRES_DAY');
    }

    return label;
  }

  TrailStatusUTCFormat(demoInstance): string {
    const expiresOn = !demoInstance.expirationDate ? this.getExpirationDate(demoInstance.dateCreated) : demoInstance.expirationDate;
    const remainingDays = getDiffDays(new Date(), expiresOn);
    let label;

    if (remainingDays <= 0) {
      label = translate('SERVICE.TRIAL_EXPIRED') + ' on ' + moment(expiresOn).utc().format('DD MMM yyyy hh:mm')+ ' UTC';
    } else {
      label = 'Trial expires on ' + moment(expiresOn).utc().format('DD MMM yyyy hh:mm')+ ' UTC';
    }
    return label;
  }

  TrailStatusMonthFormat(demoInstance): string {
    const expiresOn = !demoInstance.expirationDate ? this.getExpirationDate(demoInstance.dateCreated) : demoInstance.expirationDate;
    const remainingDays = getDiffDays(new Date(), expiresOn);
    let label;

    if (remainingDays <= 0) {
      label = translate('SERVICE.TRIAL_EXPIRED') + ' on ' + moment(expiresOn).utc().format('Do MMMM');
    } else {
      label = 'Trial expires on ' + moment(expiresOn).utc().format('Do MMMM');
    }
    return label;
  }

  deployStatusUTCFormat(demoInstance): string {
    const dateCreated = !demoInstance.dateCreated ? this.getExpirationDate(demoInstance.dateCreated) : demoInstance.dateCreated;
    const label = translate('MY_SERVICES.DEPLOYED') + ' ' + moment(dateCreated).utc().format('DD MMM yyyy hh:mm')+ ' UTC';
    return label;
  }

  createdStatusUTCFormat(blueprint): string {
    const dateCreated = blueprint?.dateCreated;
    const label = translate('MY_SERVICES.CREATED') + ' ' + moment(dateCreated).utc().format('DD MMM yyyy hh:mm')+ ' UTC';
    return label;
  }

  trailExpireDays(expirationDate): number {
    return getDiffDays(new Date(), expirationDate);
  }

  /**
   * Calculates the expiration date based on the created date
   *
   * @param dateCreated - the start date to be used for expiration date calculation purposes
   * @returns the expiration date
   */
  private getExpirationDate(dateCreated: Date): Date {
    const created = new Date(dateCreated);
    return new Date(created.setDate(created.getDate() + Constants.SERVICE_EXPIRATION_DAYS));
  }
}
